import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Typography, Box, Button, Paper, Chip, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import AdminTable from '../../components/shared/AdminTable';
import billingApi from '../../services/billingApi';
import AdminPageSkeleton from '../../components/shared/AdminPageSkeleton';

export default function BundleManagement() {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialog, setDialog] = useState(false);
  const [addPlanDialog, setAddPlanDialog] = useState(null); // bundle id
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [form, setForm] = useState({
    bundleId: '', name: '', description: '', price_cents: 0, interval_type: 'month'
  });

  useEffect(() => { loadBundles(); }, []);

  async function loadBundles() {
    try {
      const data = await billingApi.get('/api/admin/bundles');
      setBundles(data.bundles || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadPlans() {
    try {
      const data = await billingApi.get('/api/plans');
      setPlans(data.plans || []);
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleCreate() {
    try {
      await billingApi.post('/api/admin/bundles', form);
      setDialog(false);
      setForm({ bundleId: '', name: '', description: '', price_cents: 0, interval_type: 'month' });
      setSuccess('Bundle created');
      await loadBundles();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDeactivate(id) {
    if (!window.confirm('Deactivate this bundle?')) return;
    try {
      await billingApi.del(`/api/admin/bundles/${id}`);
      setSuccess('Bundle deactivated');
      await loadBundles();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleAddPlan() {
    if (!addPlanDialog || !selectedPlanId) return;
    try {
      await billingApi.post(`/api/admin/bundles/${addPlanDialog}/plans`, { planId: selectedPlanId });
      setAddPlanDialog(null);
      setSelectedPlanId('');
      setSuccess('Plan added to bundle');
      await loadBundles();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleRemovePlan(bundleId, planId) {
    if (!window.confirm('Remove this plan from the bundle?')) return;
    try {
      await billingApi.del(`/api/admin/bundles/${bundleId}/plans/${planId}`);
      setSuccess('Plan removed from bundle');
      await loadBundles();
    } catch (err) {
      setError(err.message);
    }
  }

  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: 'Bundle',
      width: 180,
      renderCell: (row) => (
        <Box>
          <Typography variant="body2" fontWeight="bold">{row.name}</Typography>
          <Typography variant="caption" color="text.secondary">{row.bundleId}</Typography>
        </Box>
      ),
    },
    {
      field: 'price_cents',
      headerName: 'Price',
      width: 100,
      type: 'numeric',
      renderCell: (row) => `$${(row.price_cents / 100).toFixed(2)}`,
    },
    {
      field: 'interval_type',
      headerName: 'Interval',
      width: 100,
      type: 'singleSelect',
      filterOptions: ['month', 'year'],
    },
    {
      field: 'plans',
      headerName: 'Component Plans',
      flex: 1,
      minWidth: 300,
      sortable: false,
      filterable: false,
      renderCell: (row) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
          {(row.plans || []).map(p => (
            <Chip
              key={p.planId}
              label={`${p.product_name}: ${p.plan_name}`}
              size="small"
              sx={{ mr: 0.5, mb: 0.5 }}
              onDelete={(e) => { e.stopPropagation(); handleRemovePlan(row.id, p.plan_id || p.bundle_plan_id); }}
            />
          ))}
          <Button
            size="small"
            onClick={(e) => { e.stopPropagation(); setAddPlanDialog(row.id); loadPlans(); }}
          >
            + Add Plan
          </Button>
        </Box>
      ),
    },
    {
      field: 'savings_cents',
      headerName: 'Savings',
      width: 120,
      type: 'numeric',
      renderCell: (row) => row.savings_cents > 0 ? (
        <Chip label={`Save $${(row.savings_cents / 100).toFixed(2)}`} color="success" size="small" />
      ) : null,
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 110,
      type: 'singleSelect',
      filterOptions: ['Active', 'Inactive'],
      valueGetter: (params) => params.row.is_active ? 'Active' : 'Inactive',
      renderCell: (row) => {
        const label = row.is_active ? 'Active' : 'Inactive';
        return <Chip label={label} color={label === 'Active' ? 'success' : 'default'} size="small" />;
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (row) => row.is_active ? (
        <Button size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeactivate(row.id); }}>
          Deactivate
        </Button>
      ) : null,
    },
  ], []);

  if (loading) return <AdminPageSkeleton />;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Bundle Management</Typography>
        <Button variant="contained" onClick={() => setDialog(true)}>Create Bundle</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper variant="outlined" sx={{ height: 500 }}>
        <AdminTable
          rows={bundles}
          columns={columns}
          defaultSort={[]}
        />
      </Paper>

      {/* Create Bundle Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Bundle</DialogTitle>
        <DialogContent>
          <TextField label="Bundle ID" required value={form.bundleId}
            onChange={(e) => setForm(f => ({ ...f, bundleId: e.target.value }))}
            fullWidth sx={{ mt: 1, mb: 2 }} placeholder="e.g., pro-combo" />
          <TextField label="Name" required value={form.name}
            onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
            fullWidth sx={{ mb: 2 }} />
          <TextField label="Description" value={form.description}
            onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
            fullWidth sx={{ mb: 2 }} multiline rows={2} />
          <TextField label="Price (cents)" type="number" value={form.price_cents}
            onChange={(e) => setForm(f => ({ ...f, price_cents: parseInt(e.target.value) || 0 }))}
            fullWidth sx={{ mb: 2 }} />
          <FormControl fullWidth>
            <InputLabel>Interval</InputLabel>
            <Select value={form.interval_type}
              onChange={(e) => setForm(f => ({ ...f, interval_type: e.target.value }))}
              label="Interval">
              <MenuItem value="month">Monthly</MenuItem>
              <MenuItem value="year">Annual</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!form.bundleId || !form.name}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Add Plan to Bundle Dialog */}
      <Dialog open={!!addPlanDialog} onClose={() => setAddPlanDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Plan to Bundle</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Select Plan</InputLabel>
            <Select value={selectedPlanId} onChange={(e) => setSelectedPlanId(e.target.value)} label="Select Plan">
              {plans.map(p => (
                <MenuItem key={p.id} value={p.id}>
                  {p.product_name}: {p.name} (${(p.price_cents / 100).toFixed(2)}/{p.interval_type})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPlanDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddPlan} disabled={!selectedPlanId}>Add</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
